import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Download, 
  Trash2, 
  RefreshCw, 
  Users, 
  Mail,
  Calendar,
  Lock,
  LogOut,
  ShoppingBag,
  Package,
  ExternalLink,
  Info
} from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSETS = {
  logo: "/logo.png",
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || isLoading) return;

    setIsLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        sessionStorage.setItem("adminAuth", "true");
        onLogin();
        toast.success("Login successful");
      }
    } catch (error) {
      setError("Invalid password");
      toast.error("Invalid password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <img src={ASSETS.logo} alt="Gaffer Athletics" className="h-12 mx-auto mb-8 opacity-80" />
          <h1 className="heading-lg text-2xl text-white mb-2">Admin Access</h1>
          <p className="text-white/40 text-sm">Enter password to continue</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="relative">
            <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="input-minimal pl-8 w-full"
              autoFocus
            />
          </div>
          
          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}
          
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <div className="spinner" />
            ) : (
              "Access Dashboard"
            )}
          </button>
        </form>
        
        <div className="mt-8 text-center">
          <a href="/" className="text-white/30 hover:text-white/50 text-sm transition-colors">
            ← Back to website
          </a>
        </div>
      </div>
    </div>
  );
};

// Orders Panel
const OrdersPanel = () => {
  const [ordersData, setOrdersData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders`);
      setOrdersData(response.data);
    } catch (error) {
      toast.error("Failed to fetch orders");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleExportOrders = () => {
    if (!ordersData?.orders?.length) {
      toast.error("No orders to export");
      return;
    }

    const csvContent = [
      ["Order #", "Product", "Size", "Customer", "Email", "Total (DKK)", "Currency Total", "Currency", "Shipping Region", "Payment Status", "Date"].join(","),
      ...ordersData.orders.map((o) => [
        o.order_number,
        o.product_name,
        o.size,
        `"${o.customer_name}"`,
        o.customer_email,
        o.total_dkk,
        o.total_in_currency,
        o.currency,
        o.shipping_region,
        o.payment_status,
        format(new Date(o.timestamp), "yyyy-MM-dd HH:mm"),
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gaffer-orders-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Orders exported");
  };

  const orders = ordersData?.orders || [];
  const paidOrders = ordersData?.paid_orders || 0;
  const sizeBreakdown = ordersData?.size_breakdown || {};

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="feature-card flex items-center gap-4">
          <div className="feature-icon">
            <ShoppingBag className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white/40 text-sm">Total Orders</p>
            <p data-testid="total-orders" className="heading-md text-2xl text-white">
              {orders.length}
            </p>
          </div>
        </div>
        
        <div className="feature-card flex items-center gap-4">
          <div className="feature-icon">
            <Package className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white/40 text-sm">Paid / Confirmed</p>
            <p data-testid="paid-orders" className="heading-md text-2xl text-white">
              {paidOrders}
            </p>
          </div>
        </div>
        
        <div className="feature-card flex items-center gap-4">
          <div className="feature-icon">
            <Calendar className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white/40 text-sm">Latest Order</p>
            <p className="heading-md text-lg text-white">
              {orders.length > 0 
                ? format(new Date(orders[0].timestamp), "MMM d, yyyy")
                : "—"
              }
            </p>
          </div>
        </div>
      </div>

      {/* Size Breakdown (only show if there are paid orders) */}
      {Object.keys(sizeBreakdown).length > 0 && (
        <div className="feature-card">
          <p className="text-white/40 text-sm mb-3">Size Breakdown (Paid Orders)</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(sizeBreakdown).map(([size, count]) => (
              <div key={size} className="border border-white/10 px-4 py-2">
                <span className="text-white text-sm font-semibold">{size}</span>
                <span className="text-white/40 text-sm ml-2">× {count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          data-testid="refresh-orders-button"
          onClick={fetchOrders}
          disabled={isLoading}
          className="btn-secondary px-4 py-2 flex items-center gap-2 text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <button
          data-testid="export-orders-button"
          onClick={handleExportOrders}
          className="btn-primary px-4 py-2 flex items-center gap-2 text-sm"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Orders Table */}
      <div className="border border-white/10 overflow-x-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <div className="spinner" style={{ borderLeftColor: '#FFFFFF' }} />
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <ShoppingBag className="w-12 h-12 text-white/20 mb-4" />
            <p className="text-white/40">No orders yet</p>
            <p className="text-white/20 text-sm mt-1">Orders will appear here once customers place pre-orders</p>
          </div>
        ) : (
          <table className="admin-table" data-testid="orders-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Size</th>
                <th>Total</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id} data-testid={`order-row-${order.id}`}>
                  <td className="text-white font-mono text-sm">{order.order_number}</td>
                  <td>
                    <div>
                      <p className="text-white text-sm">{order.customer_name}</p>
                      <p className="text-white/40 text-xs">{order.customer_email}</p>
                    </div>
                  </td>
                  <td className="text-white">{order.size}</td>
                  <td className="text-white">
                    {order.total_in_currency} {order.currency}
                  </td>
                  <td>
                    <span className={
                      order.payment_status === 'paid' 
                        ? 'inline-block px-2 py-1 text-xs uppercase tracking-wider bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'inline-block px-2 py-1 text-xs uppercase tracking-wider bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    }>
                      {order.payment_status}
                    </span>
                  </td>
                  <td className="text-white/60 text-sm">
                    {format(new Date(order.timestamp), "MMM d, yyyy · HH:mm")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// Waitlist Panel (Legacy)
const WaitlistPanel = () => {
  const [signups, setSignups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(null);

  const fetchSignups = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/waitlist`);
      setSignups(response.data);
    } catch (error) {
      toast.error("Failed to fetch signups");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSignups();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this signup?")) return;
    
    setIsDeleting(id);
    try {
      await axios.delete(`${API}/waitlist/${id}`);
      setSignups(signups.filter((s) => s.id !== id));
      toast.success("Signup deleted");
    } catch (error) {
      toast.error("Failed to delete signup");
      console.error(error);
    } finally {
      setIsDeleting(null);
    }
  };

  const handleExport = () => {
    if (signups.length === 0) {
      toast.error("No signups to export");
      return;
    }

    const csvContent = [
      ["Email", "Date", "Status"].join(","),
      ...signups.map((s) => [
        s.email,
        format(new Date(s.timestamp), "yyyy-MM-dd HH:mm"),
        s.status,
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gaffer-waitlist-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Export complete");
  };

  return (
    <div className="space-y-6">
      {/* Mailchimp Info Banner */}
      <div className="border border-blue-500/20 bg-blue-500/5 p-4 flex gap-3 items-start">
        <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-white text-sm font-semibold mb-1">Waitlist is managed via Mailchimp</p>
          <p className="text-white/50 text-sm">
            New signups are collected through the embedded Mailchimp form on the landing page. 
            Manage your full subscriber list and automated emails in your{" "}
            <a 
              href="https://mailchimp.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
            >
              Mailchimp dashboard <ExternalLink className="w-3 h-3" />
            </a>
          </p>
          <p className="text-white/30 text-xs mt-2">
            The table below shows legacy signups from before the Mailchimp migration.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="feature-card flex items-center gap-4">
          <div className="feature-icon">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white/40 text-sm">Legacy Signups</p>
            <p data-testid="total-signups" className="heading-md text-2xl text-white">
              {signups.length}
            </p>
          </div>
        </div>
        
        <div className="feature-card flex items-center gap-4">
          <div className="feature-icon">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white/40 text-sm">Pending</p>
            <p className="heading-md text-2xl text-white">
              {signups.filter(s => s.status === 'pending').length}
            </p>
          </div>
        </div>
        
        <div className="feature-card flex items-center gap-4">
          <div className="feature-icon">
            <Calendar className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white/40 text-sm">Last Signup</p>
            <p className="heading-md text-lg text-white">
              {signups.length > 0 
                ? format(new Date(signups[0].timestamp), "MMM d, yyyy")
                : "—"
              }
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          data-testid="refresh-button"
          onClick={fetchSignups}
          disabled={isLoading}
          className="btn-secondary px-4 py-2 flex items-center gap-2 text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <button
          data-testid="export-button"
          onClick={handleExport}
          className="btn-primary px-4 py-2 flex items-center gap-2 text-sm"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="border border-white/10 overflow-x-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <div className="spinner" style={{ borderLeftColor: '#FFFFFF' }} />
          </div>
        ) : signups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Users className="w-12 h-12 text-white/20 mb-4" />
            <p className="text-white/40">No legacy signups in database</p>
            <p className="text-white/20 text-sm mt-1">New signups are managed via Mailchimp</p>
          </div>
        ) : (
          <table className="admin-table" data-testid="signups-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Date</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {signups.map((signup) => (
                <tr key={signup.id} data-testid={`signup-row-${signup.id}`}>
                  <td className="text-white">{signup.name || "—"}</td>
                  <td className="text-white">{signup.email}</td>
                  <td className="text-white/60">
                    {format(new Date(signup.timestamp), "MMM d, yyyy · HH:mm")}
                  </td>
                  <td>
                    <span className={signup.status === 'pending' ? 'badge-pending' : 'badge-confirmed'}>
                      {signup.status}
                    </span>
                  </td>
                  <td className="text-right">
                    <button
                      data-testid={`delete-btn-${signup.id}`}
                      onClick={() => handleDelete(signup.id)}
                      disabled={isDeleting === signup.id}
                      className="text-white/40 hover:text-red-500 transition-colors p-2"
                    >
                      {isDeleting === signup.id ? (
                        <div className="spinner w-4 h-4" style={{ borderLeftColor: '#EF4444' }} />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState("orders");

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    onLogout();
    toast.success("Logged out");
  };

  return (
    <div data-testid="admin-dashboard" className="admin-container min-h-screen">
      {/* Header */}
      <header className="admin-header flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-6">
          <a href="/" className="text-white/60 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </a>
          <img src={ASSETS.logo} alt="Gaffer Athletics" className="h-8" />
          <div className="hidden sm:block w-px h-8 bg-white/10" />
          <h1 className="heading-md text-white text-lg">Admin Dashboard</h1>
        </div>
        
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="btn-secondary px-4 py-2 flex items-center gap-2 text-sm border-red-500/30 text-red-400 hover:bg-red-500 hover:text-white hover:border-red-500"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </header>

      {/* Tabs */}
      <div className="px-6 md:px-8 pt-6">
        <div className="flex border-b border-white/10">
          <button
            data-testid="tab-orders"
            onClick={() => setActiveTab("orders")}
            className={`px-6 py-3 text-sm uppercase tracking-wider font-semibold transition-colors border-b-2 ${
              activeTab === "orders"
                ? "text-white border-white"
                : "text-white/40 border-transparent hover:text-white/60"
            }`}
          >
            <span className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" />
              Orders
            </span>
          </button>
          <button
            data-testid="tab-waitlist"
            onClick={() => setActiveTab("waitlist")}
            className={`px-6 py-3 text-sm uppercase tracking-wider font-semibold transition-colors border-b-2 ${
              activeTab === "waitlist"
                ? "text-white border-white"
                : "text-white/40 border-transparent hover:text-white/60"
            }`}
          >
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Waitlist
            </span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 md:p-8">
        {activeTab === "orders" ? <OrdersPanel /> : <WaitlistPanel />}
      </div>
    </div>
  );
};

// Main Admin Dashboard Component
export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem("adminAuth");
    if (auth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  if (!isAuthenticated) {
    return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  return <Dashboard onLogout={() => setIsAuthenticated(false)} />;
}
